import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ==============================================================================
// >> STYLES & CONFIGURATION
// ==============================================================================
const API_BASE_URL = "https://qf-v1.onrender.com"; // Your backend API

const styles = {
  // Styles remain the same as your original file...
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', padding: '2rem', flexDirection: 'column' },
  card: { width: '100%', maxWidth: '900px', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', padding: '2.5rem', textAlign: 'left' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '1rem', marginBottom: '1.5rem' },
  title: { fontSize: '1.75rem', color: '#111', margin: 0 },
  section: { marginBottom: '2rem' },
  sectionTitle: { fontSize: '1.2rem', color: '#333', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' },
  detailText: { fontSize: '1rem', color: '#555', marginBottom: '0.5rem' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.25rem' },
  list: { listStyle: 'none', padding: 0 },
  listItem: { background: '#fafafa', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '0.5rem', fontSize: '1rem', color: '#333' },
  fileListItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa', padding: '0.5rem 1rem', borderRadius: '4px', marginBottom: '0.5rem' },
  fileName: { fontSize: '0.9rem', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginRight: '1rem' },
  removeButton: { background: 'transparent', border: 'none', color: '#f43f5e', cursor: 'pointer', fontSize: '1.2rem', fontWeight: 'bold' },
  button: { padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: 'bold', color: '#fff', background: '#f43f5e', border: 'none', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.2s', },
  logoutButton: { background: 'none', color: '#777', border: 'none', cursor: 'pointer', fontSize: '0.9rem' },
  buttonHover: { background: '#e11d48' },
  select: { width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '1rem', background: '#fff' },
  badge: { display: 'inline-block', padding: '0.2rem 0.5rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '600', background: '#f1f5f9', color: '#0f172a', marginLeft: '0.5rem' },
  pill: (bg, color = '#111827') => ({ display: 'inline-block', padding: '0.25rem 0.5rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600, background: bg, color, marginRight: '0.5rem', }),
  muted: { color: '#6b7280' },
  loadingContainer: { textAlign: 'center', fontSize: '1.2rem', color: '#555' },
  errorContainer: { textAlign: 'center', fontSize: '1.2rem', color: '#e11d48', background: '#ffe4e6', padding: '1rem', borderRadius: '8px' },
  
  // --- NEW STYLES FOR TABS ---
  tabContainer: { 
    display: 'flex', 
    gap: '0.5rem', 
    marginBottom: '1.5rem', 
    borderBottom: '1px solid #eee' 
  },
  tabButton: { 
    padding: '0.75rem 1rem', 
    border: 'none', 
    background: 'transparent', 
    cursor: 'pointer', 
    fontSize: '1rem', 
    color: '#555', 
    borderBottom: '2px solid transparent',
    marginBottom: '-1px', // Aligns with the container's border
  },
  tabButtonActive: { 
    // ...styles.tabButton, 
    padding: '0.75rem 1rem', 
    border: 'none', 
    background: 'transparent', 
    cursor: 'pointer', 
    fontSize: '1rem', 
    marginBottom: '-1px',
    color: '#f43f5e', 
    borderBottom: '2px solid #f43f5e', 
    fontWeight: 'bold' 
  },
  itemButtonBase: {
    padding: '0.25rem 0.5rem',
    fontSize: '0.8rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
    cursor: 'pointer'
  },
  itemButtonPickedUp: {
    background: '#f0fdf4',
    color: '#166534',
    border: '1px solid #bbf7d0',
  },
  itemButtonUnavailable: {
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
  },
  itemButtonDefault: {
    background: '#f9fafb',
    color: '#374151',
    border: '1px solid #e5e7eb',
  },
  clickableItemName: {
    color: '#3b82f6', // A standard blue link color
    cursor: 'pointer',
    textDecoration: 'underline',
    fontWeight: 600,
  }
};

const parseExchangeItem = (itemString) => {
  const parts = itemString.split('→');
  const returnPart = parts[0] || 'Return: N/A';
  const newPart = parts[1] || 'New: N/A';

  // Regex to remove "Return: ", "New: ", and "1x ", "2x ", etc.
  const clean = (part) => part.replace(/^(Return: |New: )/, '').replace(/^\d+x\s/, '').trim();

  return {
    returnItemName: clean(returnPart),
    newItemName: clean(newPart),
  };
};

// ==============================================================================
// >> REACT COMPONENT
// ==============================================================================

const CourierDashboard = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // --- STATE MANAGEMENT ---
  const [activeTab, setActiveTab] = useState('orders'); // 'orders', 'returns', 'exchanges'
  
  // Separate state for each data type
  const [activeOrders, setActiveOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  
  const [selectedId, setSelectedId] = useState(''); // Generic ID for the selected item
  const [etaInput, setEtaInput] = useState(''); // ✨ ADDED ETA STATE
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // NOTE: Courier profile would ideally be fetched from a `/courier/me` endpoint.
  // For now, we'll keep it static.
  const [courier] = useState({
    name: 'Demo Courier', // This would come from the API
    shift: '10:00 AM – 6:00 PM',
  });

  const [itemStatuses, setItemStatuses] = useState({});

  const handleItemClick = (imageUrl) => {
    if (imageUrl) {
      // Opens the image in a new tab
      window.open(imageUrl, '_blank', 'noopener,noreferrer');
    }
    // You could add an else { alert('No image available') } if you want
  };

  // Reset item statuses when the selected order changes
  useEffect(() => {
    setItemStatuses({});
  }, [selectedId]);

  // --- DATA FETCHING & API CALLS ---

  // 1. Fetch ALL active data when the component mounts
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const headers = { 'Authorization': `Bearer ${token}` };

        // Fetch all three data sources concurrently
        const [ordersRes, returnsRes, exchangesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/courier/orders/active`, { headers }),
          fetch(`${API_BASE_URL}/courier/returns?include_unassigned=true`, { headers }),
          fetch(`${API_BASE_URL}/courier/exchanges?include_unassigned=true`, { headers })
        ]);

        if (!ordersRes.ok || !returnsRes.ok || !exchangesRes.ok) {
          throw new Error('Failed to fetch data. Please try logging in again.');
        }

        const ordersData = await ordersRes.json();
        const returnsData = await returnsRes.json();
        const exchangesData = await exchangesRes.json();

        setActiveOrders(ordersData);
        setReturns(returnsData);
        setExchanges(exchangesData);

        // Set default selection to the first *order*, if it exists
        if (ordersData.length > 0) {
          setSelectedId(ordersData[0].id);
        }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // Determine which list to display based on the active tab
  const currentList = (() => {
    switch (activeTab) {
      case 'orders': return activeOrders;
      case 'returns': return returns;
      case 'exchanges': return exchanges;
      default: return [];
    }
  })();

  // ✨ ADDED: Keep the ETA input synced with the currently selected order/return/exchange
  useEffect(() => {
    const selectedItem = currentList.find((o) => o.id === selectedId);
    if (selectedItem) {
      setEtaInput(selectedItem.eta !== '—' ? selectedItem.eta : '');
    }
  }, [selectedId, currentList]);

  // ✨ ADDED: Handler for updating the ETA
  const handleUpdateETA = async () => {
    const token = localStorage.getItem('token');
    if (!selectedId || !token) return;

    // Determine the correct endpoint based on the active tab
    const endpoint = activeTab === 'orders' 
      ? `/courier/orders/${selectedId}/eta` 
      : `/courier/returns/${selectedId}/eta`;

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ eta: etaInput }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to update ETA.');
      }

      // Update the local state to reflect the change immediately
      const updateList = (prevList) =>
        prevList.map((item) =>
          item.id === selectedId ? { ...item, eta: etaInput } : item
        );

      if (activeTab === 'orders') setActiveOrders(updateList);
      else if (activeTab === 'returns') setReturns(updateList);
      else if (activeTab === 'exchanges') setExchanges(updateList);

      alert(`✅ ETA updated to "${etaInput}"!`);
    } catch (error) {
      console.error("Failed to update ETA:", error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  // 2. Handler for updating an *order's* status
  const handleStatusUpdate = async (newStatus) => {
    const token = localStorage.getItem('token');
    if (!selectedId || !token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/courier/orders/${selectedId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update status.');
      }
      
      // Update local *activeOrders* state for immediate UI feedback
      setActiveOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === selectedId ? { ...order, status: newStatus } : order
        )
      );
      alert(`✅ Order status updated to "${newStatus}"!`);

    } catch (err) {
      alert(`❌ Error: ${err.message}`);
    }
  };

  const handleUpdateReturnStatus = async (newStatus) => {
    const token = localStorage.getItem('token');
    if (!selectedId || !token) return;

    console.log(`(DEMO) Updating return ${selectedId} to ${newStatus}`);

    // --- LOCAL-ONLY UPDATE (FALLBACK) ---
    // This just updates the UI. You'll need to call your backend API here.
    setReturns(prevReturns =>
      prevReturns.map(ret =>
        ret.id === selectedId ? { ...ret, status: newStatus } : ret
      )
    );
    alert(`✅ (Demo) Return status updated to "${newStatus}"!`);
    
    // --- REAL API CALL (when ready) ---
    
    try {
      const response = await fetch(`${API_BASE_URL}/courier/returns/${selectedId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!response.ok) throw new Error('Failed to update return status.');
      
      // On success, update local *returns* state
      if (activeTab === 'returns') {
        setReturns(prev =>
            prev.map(ret =>
              ret.id === selectedId ? { ...ret, status: newStatus } : ret
            )
        );
      } else if (activeTab === 'exchanges') {
        setExchanges(prev =>
            prev.map(ex =>
              ex.id === selectedId ? { ...ex, status: newStatus } : ex
            )
        );
      }
      alert(`✅ ${activeTab} status updated to "${newStatus}"!`);

    } catch (err) {
      console.error(err);
      alert(`❌ Error: ${err.message}.`);
    }
    
  };
  
  // 3. Handler for receipt thread (specific to *orders*)
  const handleRequestReceiptThread = async () => {
      const token = localStorage.getItem('token');
      if (!selectedId || !token) {
          alert("Please select an order first.");
          return;
      }

      try {
          const response = await fetch(`${API_BASE_URL}/courier/orders/${selectedId}/create-receipt-thread`, {
              method: 'POST',
              headers: {
                  'Authorization': `Bearer ${token}`,
              },
          });

          const result = await response.json();

          if (!response.ok) {
              throw new Error(result.detail || 'Failed to start receipt thread.');
          }

          alert(`✅ ${result.message}`); // Display success message from the API

      } catch (err) {
          alert(`❌ Error: ${err.message}`);
      }
  };

  // 4. Handler for uploading proof (specific to *orders*)
  const handleProofUpload = async () => {
    const token = localStorage.getItem('token');
    if (uploadedFiles.length === 0 || !selectedId || !token) {
      alert("Please select files to upload first.");
      return;
    }

    const formData = new FormData();
    uploadedFiles.forEach(file => {
      formData.append('files', file); 
    });

    try {
      const response = await fetch(`${API_BASE_URL}/courier/orders/${selectedId}/upload-proof`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'File upload failed.');
      }
      
      alert(`✅ Successfully uploaded ${uploadedFiles.length} file(s).`);
      setUploadedFiles([]); // Clear files after successful upload

    } catch (err) {
      alert(`❌ Error: ${err.message}`);
    }
  };

  const toggleItemPickedUpStatus = (itemName) => {
    setItemStatuses(prev => {
      const currentStatus = prev[itemName]?.status;
      const newState = { ...prev };

      if (currentStatus === 'picked_up') {
        // Toggle off - remove from state
        delete newState[itemName];
      } else {
        // Toggle on - set to 'picked_up'
        newState[itemName] = { status: 'picked_up' };
      }
      
      return newState;
    });
  };

  // 5. Handler for marking item unavailable (specific to *orders*)
  const handleMarkUnavailable = async (itemName) => {
    const token = localStorage.getItem('token');
    // We derive selectedItem later, so we check selectedId here
    if (!selectedId || !token) { 
      alert("No order selected or you are not logged in.");
      return;
    }

    const payload = {
      item_name: itemName,
      quantity: 1, 
      refund_method: "full_refund",
      reason: "item_not_in_stock",
      note: "Courier marked as unavailable"
    };

    try {
      const response = await fetch(`${API_BASE_URL}/courier/orders/${selectedId}/item/unavailable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to mark item unavailable.');
      }
      
      const result = await response.json();
      alert(`${itemName} has been marked as unavailable. Record ID: ${result.record_id}`);

      setItemStatuses(prev => ({
        ...prev,
        [itemName]: { status: 'unavailable', note: payload.note }
      }));
      // TODO: Re-fetch order data here

    } catch (error) {
      console.error("Failed to mark item unavailable:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleSendItemStatusEmail = async () => {
    const token = localStorage.getItem('token');
    const selectedItem = activeOrders.find((o) => o.id === selectedId);

    if (!selectedItem || !token) {
      alert("No order selected or you are not logged in.");
      return;
    }

    // Build the payload from local state
    const itemsPayload = selectedItem.items.map(item => {
      const statusInfo = itemStatuses[item.name]; // Use item.name
      return {
        name: item.name, // Use item.name
        picked_up: statusInfo?.status === 'picked_up',
        unavailable: statusInfo?.status === 'unavailable',
        note: statusInfo?.note || null
      };
    });

    const emailPayload = {
      items: itemsPayload,
      eta: selectedItem.eta,
      custom_message: "Here's an update on your items:"
    };

    try {
      // 1. Send the item status email
      const response = await fetch(`${API_BASE_URL}/courier/orders/${selectedId}/items/status-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailPayload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || `Failed to send status email.`);
      }

      const result = await response.json();
      
      // 2. If email send was successful, update the main order status
      await handleStatusUpdate('Out for Delivery');

      alert(`✅ Item statuses sent to customer and order marked as 'Out for Delivery'.`);

    } catch (error) {
      console.error(`Failed to send status email:`, error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleMarkReturnIssue = async (itemName) => {
    const token = localStorage.getItem('token');
    if (!selectedId || !token) return alert("No return selected.");

    // Prompt for the required note
    const note = prompt(`Please provide a note for the issue with "${itemName}":\n(e.g., "Item was damaged", "Customer did not have box")`);
    if (!note) {
      alert("A note is required to log an issue.");
      return;
    }

    const payload = {
      item_name: itemName,
      quantity: 1, 
      reason: "courier_logged_issue", // A generic reason
      note: note // The note from the prompt
    };

    try {
      // Note: selectedId is the return_id when on the returns tab
      const response = await fetch(`${API_BASE_URL}/courier/returns/${selectedId}/item/pickup-issue`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to log return issue.');
      }
      
      const result = await response.json();
      alert(`Issue logged for ${itemName}. Record ID: ${result.record_id}`);

      // Use 'unavailable' as the status key to reuse styling
      setItemStatuses(prev => ({ ...prev, [itemName]: { status: 'unavailable', note: payload.note } }));
    
    } catch (error) {
      console.error("Failed to log return issue:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleSendReturnStatusEmail = async () => {
     const token = localStorage.getItem('token');
      // Find the selected item from the 'returns' list
       const selectedItem = returns.find((r) => r.id === selectedId);
       if (!selectedItem || !token) return alert("No return selected.");

     // Build payload from local state (same logic)
     const itemsPayload = selectedItem.items.map(item => {
       const statusInfo = itemStatuses[item.name]; // Use item.name
       return {
         name: item.name, // Use item.name
         picked_up: statusInfo?.status === 'picked_up', // 'Collected'
         unavailable: statusInfo?.status === 'unavailable', // 'Not Collected'
         note: statusInfo?.note || null
       };
 });
  const emailPayload = { items: itemsPayload, eta: selectedItem.eta };

   try {
  // Call the new returns email endpoint
  const response = await fetch(`${API_BASE_URL}/courier/returns/${selectedId}/items/status-email`, {
   method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify(emailPayload),
  });

  if (!response.ok) {
     const errData = await response.json();
   throw new Error(errData.detail || `Failed to send return status email.`);
   }
  // On success, update the main *return* status
   await handleUpdateReturnStatus('Picked Up');
   alert(`✅ Return statuses sent to customer and item marked as 'Picked Up'.`);

   } catch (error) {
   console.error(`Failed to send return status email:`, error);
      alert(`Error: ${error.message}`);
    }
   };

   const toggleExchangeItemStatus = (itemString, itemType, status) => {
    setItemStatuses(prev => {
      const currentItem = prev[itemString] || {};
      const currentTypeState = currentItem[itemType] || {};
      const newState = { ...prev };

      if (currentTypeState.status === status) {
        // Toggle off
        const updatedItem = { ...currentItem };
        delete updatedItem[itemType];
        newState[itemString] = updatedItem;
      } else {
        // Toggle on
        newState[itemString] = {
          ...currentItem,
          [itemType]: { status: status, note: null } // Set status, clear note
        };
      }
      return newState;
    });
  };

  // --- NEW ---
  // 12. New handler for "Mark New Item Unavailable" (for exchanges)
  const handleExchangeMarkNewUnavailable = async (itemString, newItemName) => {
    const token = localStorage.getItem('token');
    if (!selectedId || !token) return alert("No exchange selected.");

    const note = "Courier marked as unavailable at store";
    const reason = "item_not_in_stock";

    const payload = {
      item_name: newItemName,
      quantity: 1,
      refund_method: "none",
      reason: reason,
      note: note
    };

    try {
      // Call the NEW exchange-specific 'unavailable' endpoint
      const response = await fetch(`${API_BASE_URL}/courier/exchanges/${selectedId}/new-item/unavailable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to mark new item unavailable.');
      }
      
      const result = await response.json();
      alert(`New item "${newItemName}" marked as unavailable. Record ID: ${result.record_id}`);

      // Update the complex exchange state
      setItemStatuses(prev => {
        const currentItem = prev[itemString] || {};
        return {
          ...prev,
          [itemString]: {
            ...currentItem,
            new: { status: 'unavailable', note: note, reason: reason }
          }
        };
      });

    } catch (error) {
      console.error("Failed to mark new item unavailable:", error);
      alert(`Error: ${error.message}`);
    }
  };

  // --- NEW ---
  // 13. New handler for "Mark Return Item Issue" (for exchanges)
  const handleExchangeMarkReturnIssue = async (itemString, returnItemName) => {
    const token = localStorage.getItem('token');
    if (!selectedId || !token) return alert("No exchange selected.");

    const note = prompt(`Please provide a note for the issue with "${returnItemName}":\n(e.g., "Item was damaged", "Customer did not have box")`);
    if (!note) {
      alert("A note is required to log an issue.");
      return;
    }

    const reason = "courier_logged_issue";
    const payload = {
      item_name: returnItemName,
      quantity: 1,
      reason: reason,
      note: note
    };

    try {
      // Call the REUSABLE 'returns' issue endpoint
      const response = await fetch(`${API_BASE_URL}/courier/returns/${selectedId}/item/pickup-issue`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to log return issue.');
      }
      
      const result = await response.json();
      alert(`Issue logged for "${returnItemName}". Record ID: ${result.record_id}`);

      // Update the complex exchange state
      setItemStatuses(prev => {
        const currentItem = prev[itemString] || {};
        return {
          ...prev,
          [itemString]: {
            ...currentItem,
            return: { status: 'issue', note: note, reason: reason }
          }
        };
      });

    } catch (error) {
      console.error("Failed to log return issue:", error);
      alert(`Error: ${error.message}`);
    }
  };

  // --- NEW ---
  // 14. New handler for sending the final exchange status email
  const handleSendExchangeStatusEmail = async () => {
    const token = localStorage.getItem('token');
    const selectedItem = exchanges.find((e) => e.id === selectedId);
    if (!selectedItem || !token) return alert("No exchange selected.");

    const newItemsPayload = [];
    const returnItemsPayload = [];

    try {
       // Build the payload by parsing each item and checking its state
       // ⛔️ CHANGED: 'item' is now an object
       for (const item of selectedItem.items) {
         const { returnItemName, newItemName } = parseExchangeItem(item.name); // Use item.name
         const itemState = itemStatuses[item.name]; // Use item.name

         // Validate that a status has been set for both sub-items
         if (!itemState || !itemState.return?.status || !itemState.new?.status) {
           // ⛔️ CHANGED: Use item.name in error
           throw new Error(`Please set a status for all parts of the item: "${item.name}"`);
         }

         newItemsPayload.push({
           item_name: newItemName,
           status: itemState.new.status, // 'picked_up' or 'unavailable'
           note: itemState.new.note || null,
           reason: itemState.new.reason || null
         });
         
         returnItemsPayload.push({
           item_name: returnItemName,
           status: itemState.return.status, // 'collected' or 'issue'
           note: itemState.return.note || null,
           reason: itemState.return.reason || null
         });
       }

       const emailPayload = {
         new_items: newItemsPayload,
         return_items: returnItemsPayload,
         eta: selectedItem.eta,
         custom_message: "Here is the final update on your exchange:"
       };

       // 1. Call the NEW exchange email endpoint
       const response = await fetch(`${API_BASE_URL}/courier/exchanges/${selectedId}/items/status-email`, {
         method: 'POST',
         headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
         body: JSON.stringify(emailPayload),
       });

       if (!response.ok) {
         const errData = await response.json();
         throw new Error(errData.detail || `Failed to send exchange status email.`);
       }

       // 2. On success, update the main exchange status
       await handleUpdateReturnStatus('Picked Up');
       
       alert(`✅ Exchange statuses sent to customer and item marked as 'Picked Up'.`);

     } catch (error) {
       console.error(`Failed to send exchange status email:`, error);
       alert(`Error: ${error.message}`);
     }
  };

  // --- UTILITY & EVENT HANDLERS ---
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Set selectedId to the first item of the new list, or empty
    if (tab === 'orders') {
      setSelectedId(activeOrders[0]?.id || '');
    } else if (tab === 'returns') {
      setSelectedId(returns[0]?.id || '');
    } else if (tab === 'exchanges') {
      setSelectedId(exchanges[0]?.id || '');
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (indexToRemove) => {
    setUploadedFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };
  
  const handleMouseEnter = (e) => (e.currentTarget.style.background = styles.buttonHover.background);
  const handleMouseLeave = (e) => (e.currentTarget.style.background = styles.button.background);
  
  // --- RENDER LOGIC ---
  
  // 2. Get the fully selected item object from the current list
  const selectedItem = currentList.find((o) => o.id === selectedId);

  // 3. Determine the title for the section
  const listTitle = (() => {
    switch (activeTab) {
      case 'orders': return 'Your Orders';
      case 'returns': return 'Your Returns';
      case 'exchanges': return 'Your Exchanges';
      default: return 'Items';
    }
  })();
  
  // 4. Updated status pill logic to handle new statuses
  const statusPillStyle = (() => {
    if (!selectedItem) return styles.pill('#e5e7eb', '#111827');
    switch (selectedItem.status) {
      // Completed Statuses (Green)
      case 'Delivered':
      case 'Completed': // From returns/exchanges
      case 'Picked Up':   // From returns/exchanges
        return styles.pill('#dcfce7', '#065f46');
      
      // In-Progress Statuses (Blue)
      case 'Out for Delivery':
      case 'Out for Pickup': // From returns/exchanges
        return styles.pill('#dbeafe', '#1e40af');

      // Needs Action Statuses (Yellow)
      case 'Ready for Pickup':
      case 'Scheduled':   // From returns/exchanges
      case 'Processing':  // From returns/exchanges
      case 'Pending Approval': // From returns/exchanges
        return styles.pill('#fef3c7', '#92400e');
        
      // Default/Pending (Gray)
      default:
        return styles.pill('#e5e7eb', '#111827');
    }
  })();
  
  if (isLoading) {
    return <div style={styles.container}><div style={styles.loadingContainer}>Loading active items...</div></div>;
  }

  if (error) {
    return <div style={styles.container}><div style={styles.errorContainer}>Error: {error}</div></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Courier Dashboard</h2>
          <div>
            <span style={styles.muted}>
              {courier.name}
              <span style={styles.badge}>Shift: {courier.shift}</span>
              {/* Updated Badges */}
              <span style={styles.badge}>Orders: {activeOrders.length}</span>
              <span style={styles.badge}>Returns: {returns.length}</span>
              <span style={{ ...styles.badge }}>Exchanges: {exchanges.length}</span>
            </span>
            <button onClick={handleLogout} style={{ ...styles.logoutButton, marginLeft: '1rem' }}>
              Logout
            </button>
          </div>
        </div>

        {/* --- TABS --- */}
        <div style={styles.tabContainer}>
          <button 
            style={activeTab === 'orders' ? styles.tabButtonActive : styles.tabButton}
            onClick={() => handleTabChange('orders')}
          >
            Active Orders ({activeOrders.length})
          </button>
          <button 
            style={activeTab === 'returns' ? styles.tabButtonActive : styles.tabButton}
            onClick={() => handleTabChange('returns')}
          >
            Returns ({returns.length})
          </button>
          <button 
            style={activeTab === 'exchanges' ? styles.tabButtonActive : styles.tabButton}
            onClick={() => handleTabChange('exchanges')}
          >
            Exchanges ({exchanges.length})
          </button>
        </div>

        {/* --- DYNAMIC CONTENT BASED ON TAB --- */}
        {currentList.length === 0 ? (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>No Active {activeTab}</h3>
            <p style={styles.detailText}>There are currently no {activeTab} assigned to you. Please check back later!</p>
          </div>
        ) : (
          <>
            {/* Item Selector & Details */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>{listTitle}</h3>
              <div style={styles.grid}>
                <div>
                  <label htmlFor="itemSelect" style={{ ...styles.detailText, fontWeight: 600 }}>Select {activeTab.slice(0, -1)}</label>
                  <select id="itemSelect" style={styles.select} value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
                    {currentList.map((o) => (
                      <option key={o.id} value={o.id}>{o.id} • {o.store}</option>
                    ))}
                  </select>
                  <p style={{ ...styles.detailText, marginTop: '0.75rem' }}>
                    <strong>Placed:</strong> {selectedItem?.placedAt}
                  </p>
                </div>
                <div>
                    <p style={styles.detailText}>
                      <strong>Status:</strong> <span style={statusPillStyle}>{selectedItem?.status}</span>
                    </p>
                    <p style={styles.detailText}>
                      <strong>Customer:</strong> {selectedItem?.customerName || 'N/A'}
                    </p>
                    <p style={styles.detailText}>
                      <strong>Phone:</strong> {selectedItem?.customerPhone && selectedItem.customerPhone !== 'N/A' ? (
                        <a
                          href={`sms:${selectedItem.customerPhone}`}
                          style={{ color: '#3b82f6', textDecoration: 'none' }} // Changed color to a common blue hex code
                        >
                          {selectedItem.customerPhone}
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </p>
                    <p style={styles.detailText}>
                      <strong>Address:</strong> {
                        selectedItem?.deliveryAddress?.label && selectedItem?.deliveryAddress?.street
                          ? `${selectedItem.deliveryAddress.label} - ${selectedItem.deliveryAddress.street}`
                          : selectedItem?.deliveryAddress?.street || selectedItem?.deliveryAddress?.label || 'N/A'
                      }
                    </p>
                    {/* Conditionally show line 2 if we have a city/state */}
                    {(selectedItem?.deliveryAddress?.city || selectedItem?.deliveryAddress?.state) && (
                      <p style={{ ...styles.detailText, marginTop: '-0.25rem', paddingLeft: '1rem' }}>
                        {selectedItem.deliveryAddress.city}, {selectedItem.deliveryAddress.state} {selectedItem.deliveryAddress.zipCode}
                      </p>
                    )}
                    {/* ✨ ADDED: Conditionally show the address phone number */}
                    {selectedItem?.deliveryAddress?.phone && (
                      <p style={{ ...styles.detailText, marginTop: '-0.25rem', paddingLeft: '1rem' }}>
                        📞 <a href={`sms:${selectedItem.deliveryAddress.phone}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>
                          {selectedItem.deliveryAddress.phone}
                        </a>
                      </p>
                    )}
                    
                    {/* ✨ ADDED: ETA Input UI */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <strong style={{ fontSize: '1rem', color: '#555' }}>ETA:</strong>
                      <input
                        type="text"
                        value={etaInput}
                        onChange={(e) => setEtaInput(e.target.value)}
                        placeholder="e.g. today 4:45 PM"
                        style={{
                          padding: '0.4rem 0.6rem',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          fontSize: '0.9rem',
                          width: '160px'
                        }}
                      />
                      <button 
                        onClick={handleUpdateETA} 
                        style={{ ...styles.itemButtonBase, ...styles.itemButtonDefault, padding: '0.4rem 0.75rem' }}
                      >
                        Save
                      </button>
                      <span style={{ ...styles.muted, marginLeft: '0.5rem', fontSize: '0.9rem' }}>
                        {selectedItem?.status === 'Delivered' || selectedItem?.status === 'Completed' ? '(completed)' : ''}
                      </span>
                    </div>

                    <p style={styles.detailText}><strong>Total:</strong> {selectedItem?.total}</p>
                    <p style={styles.detailText}><strong>ID:</strong> {selectedItem?.id}</p>
                  </div>

              </div>
            </div>

            {/* Items & Notes */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Items</h3>
              <ul style={styles.list}>
                {/* ============================================= */}
                {/* >> START: MODIFIED ITEM LIST JSX << */}
                {/* ============================================= */}
                {selectedItem?.items?.map((item, index) => {
                  
                  // --- Common setup for Orders and Returns ---
                  // ⛔️ CHANGED: Use item.name as the key
                  const simpleStatus = itemStatuses[item.name]?.status;
                  const isPickedUp = simpleStatus === 'picked_up';
                  const isUnavailable = simpleStatus === 'unavailable'; // Used for 'issue' in returns

                  // --- Common setup for Exchanges ---
                  // ⛔️ CHANGED: Pass item.name to the parser
                  const { returnItemName, newItemName } = (activeTab === 'exchanges') ? parseExchangeItem(item.name) : { returnItemName: '', newItemName: '' };
                  // ⛔️ CHANGED: Use item.name as the key
                  const itemState = (activeTab === 'exchanges') ? (itemStatuses[item.name] || {}) : {};
                  const returnStatus = itemState.return?.status;
                  const newStatus = itemState.new?.status;
                  const isReturnCollected = returnStatus === 'collected';
                  const isReturnIssue = returnStatus === 'issue';
                  const isNewPickedUp = newStatus === 'picked_up';
                  const isNewUnavailable = newStatus === 'unavailable';

                  // Determine background color (logic is unchanged)
                  let rowBg = '#fafafa';
                  if (activeTab === 'orders' || activeTab === 'returns') {
                    if (isPickedUp) rowBg = '#f0fdf4';
                    else if (isUnavailable) rowBg = '#fef2f2';
                  } else if (activeTab === 'exchanges') {
                    if (isReturnIssue || isNewUnavailable) rowBg = '#fef2f2'; // Error state
                    else if (isReturnCollected && isNewPickedUp) rowBg = '#f0fdf4'; // Success state
                  }

                  // --- Define the <li> style based on tab ---
                  const liStyle = (activeTab === 'exchanges')
                    ? { ...styles.listItem, background: rowBg, padding: '1rem' }
                    : { ...styles.listItem, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: rowBg };
                  
                  return (
                    <li key={index} style={liStyle}>
                      
                      {/* --- ORDER/RETURN ITEM (MODIFIED UI) --- */}
                      {(activeTab === 'orders' || activeTab === 'returns') && (
                        <>
                          {/* ⛔️ MODIFIED: This is now clickable */}
                          <span
                            style={item.imageUrl ? styles.clickableItemName : {}}
                            onClick={() => handleItemClick(item.imageUrl)}
                          >
                            {item.name}
                          </span>
                          
                          {/* --- ORDER BUTTONS --- */}
                          {activeTab === 'orders' && (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => toggleItemPickedUpStatus(item.name)} // ⛔️ CHANGED
                                disabled={isUnavailable}
                                style={{ ...styles.itemButtonBase, ...(isPickedUp ? styles.itemButtonPickedUp : styles.itemButtonDefault) }}
                              >
                                {isPickedUp ? 'Picked Up ✔' : 'Mark Picked Up'}
                              </button>
                              <button
                                onClick={() => handleMarkUnavailable(item.name)} // ⛔️ CHANGED
                                disabled={isPickedUp}
                                style={{ ...styles.itemButtonBase, ...(isUnavailable ? styles.itemButtonUnavailable : styles.itemButtonDefault) }}
                              >
                                {isUnavailable ? 'Unavailable ❌' : 'Mark Unavailable'}
                              </button>
                            </div>
                          )}

                          {/* --- RETURN BUTTONS --- */}
                          {activeTab === 'returns' && (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => toggleItemPickedUpStatus(item.name)} // ⛔️ CHANGED
                                disabled={isUnavailable}
                                style={{ ...styles.itemButtonBase, ...(isPickedUp ? styles.itemButtonPickedUp : styles.itemButtonDefault) }}
                              >
                                {isPickedUp ? 'Returned ✔' : 'Mark Returned'}
                              </button>
                              <button
                                onClick={() => handleMarkReturnIssue(item.name)} // ⛔️ CHANGED
                                disabled={isPickedUp}
                                style={{ ...styles.itemButtonBase, ...(isUnavailable ? styles.itemButtonUnavailable : styles.itemButtonDefault) }}
                              >
                                {isUnavailable ? 'Issue Logged ❌' : 'Mark Issue'}
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      {/* --- EXCHANGE ITEM (MODIFIED LOGIC) --- */}
                      {activeTab === 'exchanges' && (
                        <>
                          {/* Row 1: Item Details (Unchanged, uses parsed names) */}
                          <div style={{ marginBottom: '0.75rem' }}>
                            <div style={{ fontWeight: '600', color: '#b91c1c' }}>
                              Return: <span style={{ fontWeight: 'normal', color: '#333' }}>{returnItemName}</span>
                            </div>
                            <div style={{ fontWeight: '600', color: '#166534', marginTop: '0.25rem' }}>
                              New: <span style={{ fontWeight: 'normal', color: '#333' }}>{newItemName}</span>
                            </div>
                          </div>
                          
                          {/* Row 2: Button Groups */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            
                            {/* Group 1: New Item Buttons */}
                            <div style={{ display: 'flex', gap: '0.5rem', border: '1px solid #d4d4d4', padding: '0.5rem', borderRadius: '4px', background: '#fff' }}>
                              <span style={{fontSize: '0.8rem', fontWeight: 600, alignSelf: 'center', color: '#166534', marginRight: '0.5rem'}}>New:</span>
                              <button
                                onClick={() => toggleExchangeItemStatus(item.name, 'new', 'picked_up')} // ⛔️ CHANGED
                                disabled={isNewUnavailable}
                                style={{ ...styles.itemButtonBase, ...(isNewPickedUp ? styles.itemButtonPickedUp : styles.itemButtonDefault) }}
                              >
                                {isNewPickedUp ? 'Picked Up ✔' : 'Mark Picked Up'}
                              </button>
                              <button
                                onClick={() => handleExchangeMarkNewUnavailable(item.name, newItemName)} // ⛔️ CHANGED
                                disabled={isNewPickedUp}
                                style={{ ...styles.itemButtonBase, ...(isNewUnavailable ? styles.itemButtonUnavailable : styles.itemButtonDefault) }}
                              >
                                {isNewUnavailable ? 'Unavailable ❌' : 'Mark Unavailable'}
                              </button>
                            </div>

                            {/* Group 2: Return Item Buttons */}
                            <div style={{ display: 'flex', gap: '0.5rem', border: '1px solid #d4d4d4', padding: '0.5rem', borderRadius: '4px', background: '#fff' }}>
                              <span style={{fontSize: '0.8rem', fontWeight: 600, alignSelf: 'center', color: '#b91c1c', marginRight: '0.5rem'}}>Return:</span>
                              <button
                                onClick={() => toggleExchangeItemStatus(item.name, 'return', 'collected')} // ⛔️ CHANGED
                                disabled={isReturnIssue}
                                style={{ ...styles.itemButtonBase, ...(isReturnCollected ? styles.itemButtonPickedUp : styles.itemButtonDefault) }}
                              >
                                {isReturnCollected ? 'Collected ✔' : 'Mark Collected'}
                              </button>
                              <button
                                onClick={() => handleExchangeMarkReturnIssue(item.name, returnItemName)} // ⛔️ CHANGED
                                disabled={isReturnCollected}
                                style={{ ...styles.itemButtonBase, ...(isReturnIssue ? styles.itemButtonUnavailable : styles.itemButtonDefault) }}
                              >
                                {isReturnIssue ? 'Issue ❌' : 'Mark Issue'}
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </li>
                  );
                })}
                {/* ============================================= */}
                {/* >> END: MODIFIED ITEM LIST JSX << */}
                {/* ============================================= */}
              </ul>
              {selectedItem?.notes && (
                <p style={{ ...styles.detailText, marginTop: '0.5rem' }}>
                  <strong>Notes:</strong> {selectedItem.notes}
                </p>
              )}
            </div>

            {/* --- "ORDERS" TAB ONLY SECTIONS --- */}
            {activeTab === 'orders' && (
              <>
                {/* File Upload Section */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Proof of Purchase & Delivery Photos</h3>
                  <input type="file" multiple ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
                  <button onClick={() => fileInputRef.current?.click()} style={styles.button} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                    Add Files...
                  </button>
                  {uploadedFiles.length > 0 && (
                      <ul style={{ ...styles.list, marginTop: '0.5rem' }}>
                        {uploadedFiles.map((file, index) => (
                          <li key={`${file.name}-${index}`} style={styles.fileListItem}>
                            <span style={styles.fileName}>{file.name} • {(file.size / 1024).toFixed(1)} KB</span>
                            <button onClick={() => handleRemoveFile(index)} style={styles.removeButton}>&times;</button>
                          </li>
                        ))}
                      </ul>
                  )}
                </div>

                {/* Actions Section */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Actions</h3>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button 
                      style={styles.button} 
                      onMouseEnter={handleMouseEnter} 
                      onMouseLeave={handleMouseLeave} 
                      onClick={handleSendItemStatusEmail}
                    >
                      Send Status & Mark Picked Up
                    </button>
                    <button style={styles.button} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={() => handleStatusUpdate('Delivered')}>
                      Mark as Delivered
                    </button>
                    <button style={styles.button} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleRequestReceiptThread}>
                      Start Receipt Thread
                    </button>
                    <button style={styles.button} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleProofUpload} disabled={uploadedFiles.length === 0}>
                      Submit Proof ({uploadedFiles.length})
                    </button>
                  </div>
                </div>
              </>
            )}
            {/* --- ✨ "RETURNS" TAB ONLY SECTIONS (Corrected) --- */}
            {activeTab === 'returns' && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Actions</h3>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button
                    style={styles.button}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={handleSendReturnStatusEmail} // Calls the new return email handler
                  >
                    Send Status & Mark Picked Up
                  </button>
                  <button
                    style={styles.button}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleUpdateReturnStatus('Completed')} // Calls the new return status handler
                  >
                    Mark as Completed
                  </button>
                </div>
              </div>
            )}

{/* --- "EXCHANGES" TAB ACTIONS (FIXED) --- */}
            {activeTab === 'exchanges' && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Actions</h3>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button
                    style={styles.button}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={handleSendExchangeStatusEmail}
                  >
                    Send Status & Mark Picked Up
                  </button>
                  <button
                    style={styles.button}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleUpdateReturnStatus('Completed')}
                  >
                    Mark as Completed
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CourierDashboard;